/**
 * Authenticated Fetch Utility
 * 
 * Provides a wrapper around fetch() that automatically includes JWT authentication
 * and handles common authentication errors. This utility ensures all API requests
 * are properly authenticated and handles token expiration gracefully.
 * 
 * Features:
 * - Automatic JWT token inclusion from sessionStorage
 * - Content-Type header management (respects FormData)
 * - Automatic 401 error handling with redirect to login
 * - Support for all standard fetch options
 * - JSON parsing helper for convenience
 * 
 * Used by:
 * - PayslipSettingsService for settings API calls
 * - Other services that need authenticated requests
 * - Components that need direct API access with authentication
 * 
 * @module authenticatedFetch
 */

/**
 * Authenticated Fetch Options Interface
 * 
 * Extends standard RequestInit to allow additional options if needed.
 * All standard fetch options (method, headers, body, etc.) are supported.
 * 
 * @interface AuthenticatedFetchOptions
 * @extends RequestInit
 */
interface AuthenticatedFetchOptions extends RequestInit {
  // Additional options can be added here if needed
}

/**
 * Performs an authenticated fetch request with JWT token.
 * 
 * Wraps the standard fetch API to automatically include JWT authentication
 * and handle authentication errors. Manages Content-Type headers appropriately
 * for JSON and FormData requests.
 * 
 * Authentication flow:
 * 1. Retrieves JWT token from sessionStorage
 * 2. Adds Authorization header if token exists
 * 3. Sets Content-Type to 'application/json' for non-FormData requests
 * 4. Preserves existing headers from options
 * 5. Handles 401 responses by clearing token and redirecting to login
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} The fetch response
 * @throws {Error} Throws error if 401 (Unauthorized) response is received
 * @async
 * 
 * @example
 * ```typescript
 * // GET request
 * const response = await authenticatedFetch('/api/users');
 * 
 * // POST request with JSON body
 * const response = await authenticatedFetch('/api/users', {
 *   method: 'POST',
 *   body: JSON.stringify({ name: 'John' })
 * });
 * 
 * // POST request with FormData
 * const formData = new FormData();
 * formData.append('file', file);
 * const response = await authenticatedFetch('/api/upload', {
 *   method: 'POST',
 *   body: formData
 * });
 * ```
 */
export const authenticatedFetch = async (url: string, options: AuthenticatedFetchOptions = {}): Promise<Response> => {
  /**
   * Get JWT token from sessionStorage.
   * Token is stored by login service when user authenticates.
   */
  const token = sessionStorage.getItem('authToken');
  
  /**
   * Prepare headers object.
   * Starts with any existing headers from options, then adds/modifies as needed.
   */
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  
  /**
   * Only add Content-Type if not sending FormData.
   * When sending FormData, browser will set Content-Type with boundary automatically.
   * Manually setting Content-Type for FormData will break multipart/form-data encoding.
   */
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  /**
   * Add authorization header if token exists.
   * Uses Bearer token format for JWT authentication.
   */
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  /**
   * Perform the fetch request with authenticated headers.
   */
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  /**
   * Handle authentication errors.
   * 
   * If server returns 401 (Unauthorized), it means:
   * - Token has expired
   * - Token is invalid
   * - User is not authenticated
   * 
   * In this case, clear local storage and redirect to login page.
   */
  if (response.status === 401) {
    // Token expired or invalid, clear storage and redirect to login
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('currentEmployee');
    window.location.href = '/login';
    throw new Error('Unauthorized - redirecting to login');
  }
  
  return response;
};

/**
 * Performs an authenticated fetch request and returns parsed JSON data.
 * 
 * Convenience wrapper around authenticatedFetch that automatically parses
 * JSON responses and handles HTTP errors. Use this for API endpoints that
 * return JSON data.
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<any>} The parsed JSON response data
 * @throws {Error} Throws error if HTTP response is not OK or if JSON parsing fails
 * @async
 * 
 * @example
 * ```typescript
 * // GET request with JSON parsing
 * const data = await authenticatedFetchJson('/api/users');
 * 
 * // POST request with JSON body and response parsing
 * const result = await authenticatedFetchJson('/api/users', {
 *   method: 'POST',
 *   body: JSON.stringify({ name: 'John' })
 * });
 * ```
 */
export const authenticatedFetchJson = async (url: string, options: AuthenticatedFetchOptions = {}): Promise<any> => {
  /**
   * Perform authenticated fetch request.
   */
  const response = await authenticatedFetch(url, options);
  
  /**
   * Check if response is OK (status 200-299).
   * If not, throw error with status information.
   */
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  /**
   * Parse and return JSON response.
   * Will throw error if response is not valid JSON.
   */
  return response.json();
};
